import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import { FiPlus } from "react-icons/fi";
import { KYDEventIcon, SocialIcon, UploadIcon } from "../AudienceIcons";
import type { AudienceSource } from "./types";

type SourceOption = {
  id: AudienceSource;
  title: string;
  description: string;
  icon: React.ReactNode;
};

const SOURCE_OPTIONS: SourceOption[] = [
  {
    id: "kyd",
    title: "KYD Data",
    description: "Create a new audience from your KYD events data",
    icon: <KYDEventIcon />,
  },
  {
    id: "fb_ig",
    title: "Facebook/Instagram",
    description: "Create a new audience from Facebook or Instagram followers",
    icon: <SocialIcon />,
  },
  {
    id: "meta_interests",
    title: "Meta Interests",
    description: "Create an audience based on Meta interests",
    icon: <SocialIcon />,
  },
  {
    id: "csv",
    title: "Upload CSV",
    description: "Upload a CSV with customer emails, phone numbers, or IDs",
    icon: <UploadIcon />,
  },
];

type AudienceSourceStepProps = {
  selectedSource: AudienceSource | null;
  onSelect: (source: AudienceSource) => void;
};

const AudienceSourceStep = ({
  selectedSource,
  onSelect,
}: AudienceSourceStepProps) => {
  return (
    <VStack w="full" spacing={2} align="stretch">
      {SOURCE_OPTIONS.map((option) => {
        const isSelected = selectedSource === option.id;
        return (
          <HStack
            key={option.id}
            w="full"
            p={[2, 4]}
            border="2px solid"
            borderColor={isSelected ? "blue.500" : "gray.200"}
            borderRadius="md"
            backgroundColor={isSelected ? "gray.50" : "white"}
            alignItems="center"
            gap={3}
            cursor="pointer"
            _hover={{ borderColor: isSelected ? "blue.500" : "blue.300" }}
            transition="all 0.2s"
            onClick={() => onSelect(option.id)}
            data-testid={`audience-source-${option.id}`}
          >
            <Box
              w="12"
              h="12"
              borderRadius="md"
              backgroundColor="#F2F2F2"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {option.icon}
            </Box>
            <VStack alignItems="flex-start" flex={1} spacing={1}>
              <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                {option.title}
              </Text>
              <Text fontWeight="medium" fontSize="sm" color="gray.400">
                {option.description}
              </Text>
            </VStack>
            <Box
              w="8"
              h="8"
              borderRadius="md"
              backgroundColor="white"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <FiPlus size={16} color="blue.500" strokeWidth={2} />
            </Box>
          </HStack>
        );
      })}
    </VStack>
  );
};

export default AudienceSourceStep;
